import { Table, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import {
  useGetByTenantAssetListQuery,
  useGetByTenantDistributionsAssetswithalertsQuery
} from 'api/rtk-api'
import { set } from 'lodash'
import { useEffect, useState } from 'react'

import { TENANT_ID } from '../../../../modules/tradebuilder-v2/constants'
import {
  fixedPrecisionRound,
  formatUnits,
  parseToFixedPrecision
} from '../../../../utilities/number'
import { NumberInput, Space, Typography } from '../../atoms'
import Icon from '../../atoms/Icon'
import { CheckboxWithLabel } from '../../molecules'
import IconButton from '../../molecules/iconButton/iconButton'
import LinkIcon from '../../molecules/linkIcon'
import { StyledTableBody } from './funds-distribution.styles'

export interface ExDateDistribution {
  capitalPerShare: number
  dividendPerShare: number
  interestPerShare: number
  readOnly?: boolean
  totalPerShare: number
}

export type FundsDistributionProps = {
  finSwitchDistribution: ExDateDistribution
  finalDistribution: ExDateDistribution
  onSubmit?: (updatedDistribution: ExDateDistribution) => void
  readOnly: boolean
}

const FundsDistribution = ({
  finSwitchDistribution,
  finalDistribution,
  onSubmit,
  readOnly
}: FundsDistributionProps) => {
  const [isApproved, setIsApproved] = useState<boolean>(false)
  const [updatedDistribution, setUpdatedDistribution] =
    useState<ExDateDistribution>(finalDistribution)
  useEffect(() => {
    setUpdatedDistribution(finalDistribution)
  }, [finalDistribution])

  const assetList = useGetByTenantAssetListQuery({ tenant: TENANT_ID })
  const assetsWithAlerts = useGetByTenantDistributionsAssetswithalertsQuery({ tenant: TENANT_ID })

  const onDistributionUpdate = (updatedValueStr: string, propertyName: string) => {
    if (updatedValueStr.includes('-')) return
    const clonedUpdate = JSON.parse(JSON.stringify(updatedDistribution)) as ExDateDistribution
    const updatedValue = parseToFixedPrecision(updatedValueStr, 6)
    set(clonedUpdate, propertyName, updatedValue)
    clonedUpdate.totalPerShare = fixedPrecisionRound(
      (clonedUpdate?.capitalPerShare ?? 0) +
        (clonedUpdate?.dividendPerShare ?? 0) +
        (clonedUpdate?.interestPerShare ?? 0),
      6
    )
    setUpdatedDistribution(clonedUpdate)
  }

  const onSubmitHandler = () => {
    if (onSubmit) {
      onSubmit(updatedDistribution)
      assetList.refetch()
      assetsWithAlerts.refetch()
    }
  }

  const onReset = () => {
    const clonedUpdate = JSON.parse(JSON.stringify(updatedDistribution)) as ExDateDistribution
    clonedUpdate.capitalPerShare = undefined
    clonedUpdate.interestPerShare = undefined
    clonedUpdate.dividendPerShare = undefined
    clonedUpdate.totalPerShare = 0
    setUpdatedDistribution(clonedUpdate)
  }

  const renderLabelCell = (labelName: string) => <TableCell key="label">{labelName}</TableCell>

  const renderReadonlyTextCell = (key: string, cellValue: number) => {
    const formattedCellValue = cellValue === undefined ? '-' : formatUnits(cellValue)
    return (
      <TableCell key={key}>
        <Typography fontSize="13px" fontWeight="800" variant="b2">
          {formattedCellValue}
        </Typography>
      </TableCell>
    )
  }

  const renderTextCell = (
    key: string,
    propertyName: string,
    cellValue: number,
    cellValueReadonly: boolean,
    onValueChange: (updatedValue: string, propertyName: string) => void
  ) => {
    const readonly = readOnly || cellValueReadonly
    const formattedCellValue = cellValue === undefined ? '-' : cellValue

    return (
      <TableCell key={key}>
        {readonly && <div>{formattedCellValue}</div>}
        {!readonly && (
          <NumberInput
            data-testid={`${propertyName}_test`}
            max=""
            readOnly={readonly}
            type="number"
            onChange={event => onValueChange(event.target.value, propertyName)}
          />
        )}
      </TableCell>
    )
  }

  return (
    <>
      <TableContainer classes={{ root: 'Q-Hub' }}>
        <Table aria-label="upcoming-distributions">
          <TableHead>
            <TableRow>
              <TableCell width="400px" />
              <TableCell width="200px">Finswitch value</TableCell>
              <TableCell width="200px">Final value*</TableCell>
            </TableRow>
          </TableHead>
          <StyledTableBody>
            <TableRow key="dividend">
              {renderLabelCell('Dividend cent per unit')}
              {renderReadonlyTextCell('finswitch', finSwitchDistribution.dividendPerShare)}
              {renderTextCell(
                'final',
                'dividendPerShare',
                updatedDistribution.dividendPerShare,
                updatedDistribution.readOnly,
                onDistributionUpdate
              )}
            </TableRow>
            <TableRow key="interest">
              {renderLabelCell('Interest cent per unit')}
              {renderReadonlyTextCell('finswitch', finSwitchDistribution.interestPerShare)}
              {renderTextCell(
                'final',
                'interestPerShare',
                updatedDistribution.interestPerShare,
                updatedDistribution.readOnly,
                onDistributionUpdate
              )}
            </TableRow>
            <TableRow key="capital">
              {renderLabelCell('Capital cent per unit')}
              {renderReadonlyTextCell('finswitch', finSwitchDistribution.capitalPerShare)}
              {renderTextCell(
                'final',
                'capitalPerShare',
                updatedDistribution.capitalPerShare,
                updatedDistribution.readOnly,
                onDistributionUpdate
              )}
            </TableRow>
            <TableRow key="totals">
              <TableCell key="label">
                <Typography fontSize="13px" fontWeight="800" variant="b2">
                  Total
                </Typography>
              </TableCell>
              {renderReadonlyTextCell('finswitch', finSwitchDistribution.totalPerShare)}
              {renderReadonlyTextCell('finswitch', updatedDistribution.totalPerShare)}
            </TableRow>
          </StyledTableBody>
        </Table>
      </TableContainer>
      {!readOnly && !finalDistribution.readOnly && (
        <Space mt={6}>
          <Space mb={5}>
            <CheckboxWithLabel
              checked={isApproved}
              data-testid="confirm-distribution-checkbox"
              onChange={val => setIsApproved(val)}
            >
              Please confirm the values to continue. Once set, they cannot be changed.
            </CheckboxWithLabel>
          </Space>
          <Space isflex alignItems="center" flexDirection="row">
            <IconButton
              disabled={!isApproved}
              icon={<Icon name="chevronRight" size="small" />}
              iconPosition="right"
              operationType="progress"
              text="Submit"
              onClick={onSubmitHandler}
            />
            <Space isflex alignItems="center" ml={6}>
              <LinkIcon icon={null} iconPosition={null} text="Ignore" onClick={onReset} />
            </Space>
          </Space>
        </Space>
      )}
    </>
  )
}

export default FundsDistribution
